import type { TFunction } from 'i18next';
import { z } from 'zod';
import {
  type ConversationTranscript,
  type EditablePhraseType,
  PHRASE_TYPES,
  type PhraseCreateRequest,
  type PhraseUpdateRequest,
} from '@/entities/conversations';
import type { EditedInterval } from '@/features/DialogSession';
import type { TranscriptRecordFormValues } from './types';

/** Поля формы, которые пользователь менял, — `formState.dirtyFields` */
export type RecordDirtyFields = Partial<Record<keyof TranscriptRecordFormValues, boolean>>;

/** Значения формы существующей записи */
export const buildRecordFormValues = (record: ConversationTranscript): TranscriptRecordFormValues => ({
  personId: record.person?.id ?? null,
  text: record.text ?? '',
  startedAt: record.startedAt,
  finishedAt: record.finishedAt ?? null,
  interruptedPhraseId: record.interruptedPhraseId ?? null,
  interruptionPhraseId: record.interruptionPhraseId ?? null,
});

/**
 * Значения формы новой записи: границы заданы заранее, остальные поля пусты; у вынесенной реплики
 * текст — вынесенный фрагмент.
 */
export const buildNewRecordFormValues = (
  { startedAt, finishedAt }: Pick<TranscriptRecordFormValues, 'startedAt' | 'finishedAt'>,
  text = '',
): TranscriptRecordFormValues => ({
  personId: null,
  text,
  startedAt,
  finishedAt,
  interruptedPhraseId: null,
  interruptionPhraseId: null,
});

/**
 * Схема формы записи расшифровки: заполнены все поля вида записи, начало не позже конца, перебитая
 * и перебивающая реплики различны.
 */
export const createRecordSchema = (t: TFunction<readonly ['dialogs', 'common']>, type: EditablePhraseType) =>
  z
    .object({
      personId: z.number().nullable(),
      text: z.string(),
      startedAt: z.number().nullable(),
      finishedAt: z.number().nullable(),
      interruptedPhraseId: z.number().nullable(),
      interruptionPhraseId: z.number().nullable(),
    })
    .superRefine((values, ctx) => {
      const addIssue = (field: keyof TranscriptRecordFormValues, message: string) =>
        ctx.addIssue({ code: 'custom', path: [field], message });

      if (values.startedAt === null) addIssue('startedAt', t('common:required'));
      if (values.finishedAt === null) addIssue('finishedAt', t('common:required'));
      if (values.startedAt !== null && values.finishedAt !== null && values.startedAt > values.finishedAt) {
        addIssue('finishedAt', t('dialogs:transcriptEdit.finishBeforeStart'));
      }

      if (type === PHRASE_TYPES.PHRASE) {
        if (values.personId === null) addIssue('personId', t('common:required'));
        if (!values.text.trim()) addIssue('text', t('common:required'));
      }

      if (type === PHRASE_TYPES.INTERRUPTION) {
        if (values.interruptedPhraseId === null) addIssue('interruptedPhraseId', t('common:required'));
        if (values.interruptionPhraseId === null) addIssue('interruptionPhraseId', t('common:required'));
        if (values.interruptedPhraseId !== null && values.interruptedPhraseId === values.interruptionPhraseId) {
          addIssue('interruptionPhraseId', t('dialogs:transcriptEdit.samePhrases'));
        }
      }
    }) satisfies z.ZodType<TranscriptRecordFormValues>;

/**
 * Отрезок записи по границам формы; null — границы не заданы целиком или начало позже конца.
 */
export const readFormInterval = (startedAt: number | null, finishedAt: number | null): EditedInterval | null =>
  startedAt !== null && finishedAt !== null && startedAt <= finishedAt ? { startedAt, finishedAt } : null;

/** Значения берутся из формы, уже прошедшей схему: пустых обязательных полей в них нет */
const readRequired = (value: number | null): number => value ?? 0;

/** Тело добавления записи: границы и поля её вида — поля чужого вида сервер не принимает */
export const buildCreateRequest = (
  type: EditablePhraseType,
  values: TranscriptRecordFormValues,
): PhraseCreateRequest => {
  const bounds = { startedAt: readRequired(values.startedAt), finishedAt: readRequired(values.finishedAt) };
  switch (type) {
    case PHRASE_TYPES.PHRASE:
      return { type, ...bounds, personId: values.personId ?? undefined, text: values.text };
    case PHRASE_TYPES.INTERRUPTION:
      return {
        type,
        ...bounds,
        interruptedPhraseId: values.interruptedPhraseId ?? undefined,
        interruptionPhraseId: values.interruptionPhraseId ?? undefined,
      };
    case PHRASE_TYPES.PAUSE:
      return { type, ...bounds };
  }
};

/** Тело правки записи: только изменённые поля её вида; пустое — менять нечего */
export const buildUpdateRequest = (
  type: EditablePhraseType,
  values: TranscriptRecordFormValues,
  dirtyFields: RecordDirtyFields,
): PhraseUpdateRequest => {
  const request: PhraseUpdateRequest = {};
  if (dirtyFields.startedAt && values.startedAt !== null) request.startedAt = values.startedAt;
  if (dirtyFields.finishedAt && values.finishedAt !== null) request.finishedAt = values.finishedAt;
  if (type === PHRASE_TYPES.PHRASE) {
    if (dirtyFields.personId && values.personId !== null) request.personId = values.personId;
    if (dirtyFields.text) request.text = values.text;
  }
  if (type === PHRASE_TYPES.INTERRUPTION) {
    if (dirtyFields.interruptedPhraseId && values.interruptedPhraseId !== null) {
      request.interruptedPhraseId = values.interruptedPhraseId;
    }
    if (dirtyFields.interruptionPhraseId && values.interruptionPhraseId !== null) {
      request.interruptionPhraseId = values.interruptionPhraseId;
    }
  }
  return request;
};

/**
 * Тело выноса фрагмента: новая реплика и правка исходной — её текст всегда, остальные поля — только
 * изменённые. Обе реплики сохраняются одним запросом.
 */
export const buildSplitRequest = (
  sourceId: number,
  sourceValues: TranscriptRecordFormValues,
  sourceDirtyFields: RecordDirtyFields,
  splitValues: TranscriptRecordFormValues,
): PhraseCreateRequest => {
  const { text, ...sourceChanges } = buildUpdateRequest(PHRASE_TYPES.PHRASE, sourceValues, sourceDirtyFields);
  return {
    ...buildCreateRequest(PHRASE_TYPES.PHRASE, splitValues),
    splitFrom: { phraseId: sourceId, text: text ?? sourceValues.text, ...sourceChanges },
  };
};

/**
 * Вынос выделенного фрагмента из текста реплики: фрагмент и текст, который остаётся у исходной.
 * null — выносить нечего: выделение пустое или забирает весь текст, а исходная пустой остаться не может.
 */
export const splitPhraseText = (
  text: string,
  selectionStart: number,
  selectionEnd: number,
): { fragment: string; rest: string } | null => {
  const fragment = text.slice(selectionStart, selectionEnd).trim();
  const rest = `${text.slice(0, selectionStart).trimEnd()} ${text.slice(selectionEnd).trimStart()}`.trim();
  return fragment && rest ? { fragment, rest } : null;
};
